import React, { useCallback, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useAppAuth } from '@/modules/auth/hooks/useAppAuth';
import { authJournal } from '@/modules/auth/journal';
import { IHistory } from '@/services/history';
import { GuestLayout } from '@src-common/view/layouts/Guest';

import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { RegistrationForm } from './components/RegistrationForm';
import { SignInForm } from './components/SignInForm';
import { REGISTER_ROUTE } from './routes';
import s from './style.module.css';

/** What one form hands to the other across the navigation between them. */
interface AuthHandoff {
  prefillEmail?: string;
}

const Auth: React.FC = () => {
  const { isAuthenticated } = useAppAuth();

  const history = useHistory() as IHistory;
  const location = useLocation<AuthHandoff | undefined>();

  // Carried by the navigation rather than held here: the same component serves
  // both routes, so state of its own would outlive the hand-off and follow the
  // user into an ordinary Sign up from the header - where the registration's
  // auto-start would send a code to an address they never entered.
  const prefillEmail = location.state?.prefillEmail ?? '';

  // The view lives in the URL (named after the b2b app's /register), so the
  // registration is linkable and the back button walks between the two.
  const isRegisterView = location.pathname === REGISTER_ROUTE;

  const handleRegister = useCallback(
    (email: string) => {
      history.push(REGISTER_ROUTE, { prefillEmail: email } satisfies AuthHandoff);
    },
    [history],
  );

  const handleSignIn = useCallback(
    (email: string) => {
      history.push('/login', { prefillEmail: email } satisfies AuthHandoff);
    },
    [history],
  );

  useEffect(() => {
    if (isAuthenticated) {
      const backUrl = new URLSearchParams(history.location.search).get('back_url') ?? '';
      const isRolledBack = history.rollback();

      authJournal.step('signed_in_return', {
        back_url: backUrl,
        rolled_back: isRolledBack,
      });

      if (!isRolledBack) {
        history.replace('/');
      }
    }
  }, [isAuthenticated, history]);

  return (
    <GuestLayout>
      <div className={s.auth} data-test-id="auth">
        <Header />
        {isRegisterView ? (
          <RegistrationForm initialEmail={prefillEmail} onSignIn={handleSignIn} />
        ) : (
          <SignInForm initialEmail={prefillEmail} onRegister={handleRegister} />
        )}
        <Footer />
      </div>
    </GuestLayout>
  );
};

export { Auth };
